import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { Button } from "reactstrap";
import { toast } from "react-toastify";
import { AssignAgentModalProps } from '../interfaces';
import ModalCreate from "../../../../Components/Common/Modal";
import { whatsappApi } from '../../api/whatsapp-api';

const AssignAgentModal: React.FC<AssignAgentModalProps> = ({
    isModalOpen,
    setIsModalOpen,
    selectedContact,
    selectedContactId,
    loadContacts
}) => {
    const [availableAgents, setAvailableAgents] = useState<any[]>([]);
    const [selectedAgent, setSelectedAgent] = useState<string>('');
    const [loadingAgents, setLoadingAgents] = useState(false);
    const [assigningAgent, setAssigningAgent] = useState(false);
    const [assignError, setAssignError] = useState<string>('');
    const [progress, setProgress] = useState(0);
    const [isHolding, setIsHolding] = useState(false);
    const [agentSearchTerm, setAgentSearchTerm] = useState<string>('');
    
    const progressTimeoutRef = useRef<NodeJS.Timeout | null>(null);
    const progressIntervalRef = useRef<NodeJS.Timeout | null>(null);

    const loadAvailableAgents = useCallback(async () => {
        if (!selectedContactId) return;

        setLoadingAgents(true);
        setAssignError('');
        try {
            const response = await whatsappApi.getAgent();
            setAvailableAgents(response || []);
        } catch (error) {
            console.error('Error cargando agentes:', error);
            setAssignError('No se pudieron cargar los agentes');
            setAvailableAgents([]);
        } finally {
            setLoadingAgents(false);
        }
    }, [selectedContactId]);

    useEffect(() => {
        if (isModalOpen && selectedContactId) {
            loadAvailableAgents();
            loadContacts();
        }
    }, [isModalOpen, selectedContactId, loadAvailableAgents, loadContacts]);

    useEffect(() => {
        return () => {
            if (progressTimeoutRef.current) clearTimeout(progressTimeoutRef.current);
            if (progressIntervalRef.current) clearInterval(progressIntervalRef.current);
        };
    }, []);

    const handleAssignAgent = useCallback(async () => {
        if (!selectedContactId || !selectedAgent) {
            setAssignError('Debe seleccionar un agente');
            return;
        }

        const activeConversation = Array.isArray(selectedContact?.conversations)
            ? selectedContact.conversations.find((c: { id?: string; closedAt?: string | null }) => !c.closedAt) || selectedContact.conversations[0]
            : (selectedContact?.conversations && typeof selectedContact.conversations === 'object' && (selectedContact.conversations as any).id)
                ? selectedContact.conversations
                : null;

        const conversationId = activeConversation?.id;

        if (!conversationId) {
            setAssignError('No se encontró una conversación activa');
            return;
        }

        setAssigningAgent(true);
        setAssignError('');
        try {
            await whatsappApi.assignAgent(conversationId, selectedAgent);
            setIsModalOpen(false);
            setSelectedAgent('');
            loadContacts();
            toast.success('Agente asignado exitosamente');
        } catch (error: any) {
            console.error('Error asignando agente:', error);
            const msg = error?.response?.data?.message || 'Error al asignar el agente';
            setAssignError(msg);
        } finally {
            setAssigningAgent(false);
        }
    }, [selectedContactId, selectedAgent, selectedContact, loadContacts, setIsModalOpen]);

    const handleMouseDown = () => {
        if (assigningAgent || !selectedAgent || loadingAgents) {
            return;
        }

        setIsHolding(true);
        setProgress(0);

        const holdTime = 1500;
        const intervalTime = 50;
        const increment = (intervalTime / holdTime) * 100;

        progressIntervalRef.current = setInterval(() => {
            setProgress(prev => {
                if (prev >= 100) return 100;
                return prev + increment;
            });
        }, intervalTime);

        progressTimeoutRef.current = setTimeout(() => {
            if (progressIntervalRef.current) clearInterval(progressIntervalRef.current);
            setProgress(100);
            setIsHolding(false);
            handleAssignAgent();
        }, holdTime);
    };

    const handleMouseUp = () => {
        if (progressTimeoutRef.current) clearTimeout(progressTimeoutRef.current);
        if (progressIntervalRef.current) clearInterval(progressIntervalRef.current);
        setProgress(0);
        setIsHolding(false);
    };

    const handleMouseLeave = () => {
        handleMouseUp();
    };

    // Separar agentes activos e inactivos
    const { activeAgents, inactiveAgents, totalFiltered } = useMemo(() => {
        const filtered = availableAgents.filter(agent => {
            if (!agentSearchTerm.trim()) return true;
            const term = agentSearchTerm.toLowerCase();
            const nameMatch = agent.name?.toLowerCase().includes(term);
            const emailMatch = agent.email?.toLowerCase().includes(term);
            return nameMatch || emailMatch;
        });

        const active = filtered.filter(a => Boolean(a.available));
        const inactive = filtered.filter(a => !Boolean(a.available));

        return { activeAgents: active, inactiveAgents: inactive, totalFiltered: filtered.length };
    }, [availableAgents, agentSearchTerm]);

    const handleClose = () => {
        setIsModalOpen(false);
        setSelectedAgent('');
    };

    const handleCancel = () => {
        setIsModalOpen(false);
        setSelectedAgent('');
    };

    const renderAgentCard = (agent: any) => {
        const isSelected = selectedAgent === agent.id;
        const isAvailable = Boolean(agent.available);

        return (
            <label
                key={agent.id}
                htmlFor={`agent-${agent.id}`}
                className="border rounded-3 p-3 d-flex justify-content-between align-items-center position-relative w-100 mb-0"
                style={{
                    cursor: 'pointer',
                    transition: 'all .2s ease',
                    borderColor: isSelected ? '#25D366' : '#e9ecef',
                    backgroundColor: isSelected ? '#f0fdf4' : '#fff',
                    boxShadow: isSelected ? '0 0 0 3px rgba(37, 211, 102, 0.12)' : '0 1px 2px rgba(0,0,0,0.04)',
                    opacity: isAvailable ? 1 : 0.88
                }}
            >
                <div className="d-flex align-items-center gap-3 min-w-0">
                    <div className="form-check m-0 flex-shrink-0">
                        <input
                            className="form-check-input"
                            type="radio"
                            name="agent"
                            id={`agent-${agent.id}`}
                            value={agent.id}
                            checked={isSelected}
                            onChange={(e) => setSelectedAgent(e.target.value)}
                            style={{ cursor: 'pointer' }}
                        />
                    </div>

                    <div className="d-flex align-items-center gap-3 min-w-0">
                        <div
                            className="rounded-circle d-flex align-items-center justify-content-center flex-shrink-0 position-relative text-white"
                            style={{
                                width: '40px',
                                height: '40px',
                                backgroundColor: isAvailable ? '#d19221' : '#6c757d'
                            }}
                        >
                            <i className="ri-user-3-fill fs-18"></i>
                            <span
                                className={`position-absolute bottom-0 end-0 rounded-circle border border-2 border-white ${isAvailable ? 'bg-success' : 'bg-secondary'}`}
                                style={{ width: '10px', height: '10px' }}
                            />
                        </div>
                        <div className="min-w-0">
                            <div className="fw-semibold text-dark text-truncate">
                                {agent.name}
                            </div>
                            {agent.email && (
                                <div className="small text-muted text-truncate" style={{ fontSize: '12px' }}>
                                    {agent.email}
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                <div className="d-flex align-items-center gap-2 flex-shrink-0 ms-2">
                    <span
                        className={`badge ${isAvailable ? 'bg-success-subtle text-success' : 'bg-secondary-subtle text-secondary'} border px-2 py-1 rounded-pill`}
                        style={{ fontSize: '11px', fontWeight: 500 }}
                    >
                        <i className={`ri-circle-fill fs-8 me-1 align-middle ${isAvailable ? 'text-success' : 'text-secondary'}`}></i>
                        {isAvailable ? 'Activo' : 'Inactivo'}
                    </span>
                </div>
            </label>
        );
    };

    return (
        <ModalCreate
            title="Escalar Conversación"
            show={isModalOpen}
            handle={handleClose}
            footer={
                <div className="d-flex justify-content-end gap-2 w-100">
                    <Button
                        type="button"
                        onClick={handleCancel}
                        className="btn btn-light px-4 rounded-3"
                    >
                        Cancelar
                    </Button>

                    <button
                        type="button"
                        className="btn bg bg-primary text-white px-4 rounded-3 position-relative overflow-hidden border-0"
                        id="assign-btn"
                        disabled={
                            !selectedAgent ||
                            assigningAgent ||
                            loadingAgents
                        }
                        onMouseDown={handleMouseDown}
                        onMouseUp={handleMouseUp}
                        onMouseLeave={handleMouseLeave}
                        onClick={(e) => e.preventDefault()}
                        style={{
                            userSelect: 'none',
                            minWidth: '170px',
                            fontWeight: 500,
                            cursor: (!selectedAgent || assigningAgent || loadingAgents) ? 'not-allowed' : 'pointer'
                        }}
                    >
                        {assigningAgent ? (
                            <>
                                <div
                                    className="spinner-border spinner-border-sm me-2"
                                    role="status"
                                ></div>
                                Transfiriendo...
                            </>
                        ) : (
                            <>
                                {isHolding && (
                                    <div
                                        className="position-absolute top-0 start-0 h-100 bg-white"
                                        style={{
                                            width: `${progress}%`,
                                            transition: 'none',
                                            opacity: 0.2
                                        }}
                                    />
                                )}
                                {isHolding ? 'Mantén para asignar...' : 'Asignar Agente'}
                            </>
                        )}
                    </button>
                </div>
            }
        >
            <div className="px-1">
                {/* ERROR */}
                {assignError && (
                    <div
                        className="alert alert-danger border-0 rounded-3 mb-4"
                        role="alert"
                        style={{
                            backgroundColor: '#fee',
                            borderColor: '#fcc',
                            color: '#c33'
                        }}
                    >
                        <div className="d-flex align-items-center">
                            <i className="ri-error-warning-line me-2"></i>
                            {assignError}
                        </div>
                    </div>
                )}

                {/* CONTACTO INFO CARD */}
                <div
                    className="rounded-4 p-3 mb-3 border-0 shadow-sm"
                    style={{
                        background: 'linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)',
                        position: 'relative',
                        overflow: 'hidden'
                    }}
                >
                    <div className="small text-uppercase fw-semibold text-muted mb-1" style={{ fontSize: '11px' }}>
                        Conversación actual
                    </div>
                    <div className="d-flex align-items-center gap-3">
                        <div
                            className="rounded-circle bg-primary d-flex align-items-center justify-content-center flex-shrink-0"
                            style={{width: '42px', height: '42px'}}>
                            <i className="ri-message-3-fill text-white fs-5"></i>
                        </div>
                        <div className="min-w-0">
                            <div className="fw-bold text-dark fs-15 text-truncate">
                                {selectedContact?.name || selectedContact?.waId}
                            </div>
                            <div className="small text-muted">
                                {selectedContact?.waId}
                            </div>
                        </div>
                    </div>
                </div>

                {/* AGENTES SELECTION */}
                <div className="mb-2">
                    <div className="d-flex align-items-center justify-content-between mb-3">
                        <div>
                            <div className="fw-bold text-dark fs-15">
                                Seleccionar agente
                            </div>
                            <div className="small text-muted">
                                Elige a quién transferir la conversación (activos o inactivos)
                            </div>
                        </div>
                    </div>

                    {loadingAgents ? (
                        <div className="text-center py-5">
                            <div className="d-flex justify-content-center mb-3">
                                <div className="spinner-border" role="status"
                                     style={{color: '#25D366'}}>
                                    <span className="visually-hidden">Cargando...</span>
                                </div>
                            </div>
                            <div className="small text-muted">
                                Cargando agentes...
                            </div>
                        </div>
                    ) : availableAgents.length === 0 ? (
                        <div className="text-center py-5">
                            <div
                                className="rounded-circle bg-light d-flex align-items-center justify-content-center mx-auto mb-3"
                                style={{width: '64px', height: '64px'}}>
                                <i className="ri-user-unfollow-line text-muted fs-2"></i>
                            </div>
                            <div className="text-muted">
                                No hay agentes registrados
                            </div>
                        </div>
                    ) : (
                        <div>
                            {/* Barra de búsqueda */}
                            <div className="position-relative mb-3">
                                <input
                                    type="text"
                                    className="form-control rounded-3 ps-5"
                                    placeholder="Buscar agente por nombre..."
                                    value={agentSearchTerm}
                                    onChange={(e) => setAgentSearchTerm(e.target.value)}
                                    style={{
                                        fontSize: '14px',
                                        border: '1px solid #e9ecef',
                                        paddingTop: '10px',
                                        paddingBottom: '10px',
                                        transition: 'all 0.2s ease'
                                    }}
                                    onFocus={(e) => {
                                        e.target.style.borderColor = '#25D366';
                                        e.target.style.boxShadow = '0 0 0 3px rgba(37, 211, 102, 0.1)';
                                    }}
                                    onBlur={(e) => {
                                        e.target.style.borderColor = '#e9ecef';
                                        e.target.style.boxShadow = 'none';
                                    }}
                                />
                                <i
                                    className="ri-search-line position-absolute text-muted"
                                    style={{
                                        left: '16px',
                                        top: '50%',
                                        transform: 'translateY(-50%)',
                                        fontSize: '16px'
                                    }}
                                ></i>
                                {agentSearchTerm && (
                                    <button
                                        type="button"
                                        className="btn btn-link position-absolute end-0 top-50 translate-middle-y text-muted p-2 pe-3 text-decoration-none"
                                        onClick={() => setAgentSearchTerm('')}
                                    >
                                        <i className="ri-close-line fs-16"></i>
                                    </button>
                                )}
                            </div>
                            
                            {totalFiltered === 0 ? (
                                <div className="text-center py-4">
                                    <i className="ri-search-eye-line text-muted fs-2 mb-2"></i>
                                    <div className="text-muted small">
                                        No se encontraron agentes con "{agentSearchTerm}"
                                    </div>
                                </div>
                            ) : (
                                <div
                                    className="d-flex flex-column gap-3 pe-1"
                                    style={{
                                        maxHeight: '340px',
                                        overflowY: 'auto'
                                    }}
                                >
                                    {/* SECCIÓN: AGENTES ACTIVOS (ARRIBA) */}
                                    {activeAgents.length > 0 && (
                                        <div>
                                            <div className="d-flex align-items-center gap-2 mb-2">
                                                <span className="badge bg-success-subtle text-success border border-success-subtle rounded-pill px-2 py-1" style={{ fontSize: '11px' }}>
                                                    <i className="ri-checkbox-circle-fill me-1 align-middle"></i>
                                                    Activos ({activeAgents.length})
                                                </span>
                                                <hr className="flex-grow-1 my-0 text-muted opacity-25" />
                                            </div>
                                            <div className="d-flex flex-column gap-2">
                                                {activeAgents.map(renderAgentCard)}
                                            </div>
                                        </div>
                                    )}

                                    {/* SECCIÓN: AGENTES INACTIVOS (ABAJO) */}
                                    {inactiveAgents.length > 0 && (
                                        <div>
                                            <div className="d-flex align-items-center gap-2 mb-2">
                                                <span className="badge bg-secondary-subtle text-secondary border border-secondary-subtle rounded-pill px-2 py-1" style={{ fontSize: '11px' }}>
                                                    <i className="ri-pause-circle-line me-1 align-middle"></i>
                                                    Inactivos ({inactiveAgents.length})
                                                </span>
                                                <hr className="flex-grow-1 my-0 text-muted opacity-25" />
                                            </div>
                                            <div className="d-flex flex-column gap-2">
                                                {inactiveAgents.map(renderAgentCard)}
                                            </div>
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    )}
                </div>
            </div>
        </ModalCreate>
    );
};

export default AssignAgentModal;
